import { useState, useRef } from 'react';
import Avatar from './Avatar';

const ProfilePhotoUploader = ({ user, onPhotoUpdate }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('File size must be less than 5MB');
      return;
    }

    await uploadPhoto(file);
  };

  const uploadPhoto = async (file) => {
    console.log('🚀 UPLOAD FLOW DEBUG - Starting photo upload...');
    console.log('📁 File details:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: new Date(file.lastModified).toISOString()
    });
    console.log('👤 User ID:', user?.id);
    console.log('🔗 Upload URL:', `${backendUrl}/api/users/profile/upload-photo`);
    
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      console.log('📦 FormData prepared, making upload request...');
      
      const token = localStorage.getItem('token');
      console.log('🎫 Auth token available:', !!token);
      console.log('🎫 Token length:', token ? token.length : 0);

      const response = await fetch(`${backendUrl}/api/users/profile/upload-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      console.log('📡 Upload response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Upload successful, backend response:', result);
        console.log('🖼️ Profile image URL returned:', result.profile_image_url);
        
        // Call the callback to update parent component
        console.log('🔄 Calling onPhotoUpdate callback...');
        onPhotoUpdate && onPhotoUpdate(result.profile_image_url);
        
        setUploadProgress(100);
        console.log('✅ Upload progress set to 100%');
        
        // Small delay to show 100% progress
        setTimeout(() => {
          console.log('🏁 Upload process completed, cleaning up UI...');
          setIsUploading(false);
          setUploadProgress(0);
        }, 500);
      } else {
        console.error('❌ Upload failed with non-OK status');
        const errorText = await response.text();
        console.error('❌ Error response body:', errorText);
        
        let error;
        try {
          const errorJson = JSON.parse(errorText);
          error = new Error(errorJson.detail || 'Failed to upload photo');
        } catch (e) {
          error = new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
        }
        throw error;
      }
    } catch (error) {
      console.error('💥 UPLOAD FLOW DEBUG - Photo upload error:', error);
      console.error('💥 Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      alert(`Failed to upload photo: ${error.message}`);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const deletePhoto = async () => {
    if (!confirm('Are you sure you want to remove your profile photo?')) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/users/profile/photo`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        onPhotoUpdate && onPhotoUpdate(null);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete photo');
      }
    } catch (error) {
      console.error('Photo delete error:', error);
      alert(`Failed to delete photo: ${error.message}`);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-700 mb-4">
        Profile Photo
      </div>

      <div className="flex items-center space-x-6">
        {/* Current Photo Display */}
        <div className="flex-shrink-0">
          <Avatar user={user} size="xlarge" />
        </div>

        {/* Upload Area */}
        <div className="flex-1">
          <div
            className={`
              relative border-2 border-dashed rounded-lg p-6 text-center transition-colors
              ${dragActive ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-gray-400'}
              ${isUploading ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
            `}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => !isUploading && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileSelect}
              disabled={isUploading}
            />

            {isUploading ? (
              <div className="space-y-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
                <div className="text-sm text-gray-600">Uploading...</div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-teal-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="mx-auto h-12 w-12 text-gray-400">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium text-teal-600 hover:text-teal-500">
                    Click to upload
                  </span>
                  {' or drag and drop'}
                </div>
                <p className="text-xs text-gray-500">
                  PNG, JPG, WebP up to 5MB
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 mt-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Choose File
            </button>
            
            {user?.profile_image && (
              <button
                type="button"
                onClick={deletePhoto}
                disabled={isUploading}
                className="px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Remove Photo
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="text-xs text-gray-500">
        Your profile photo will be cropped to a square and used across the platform in posts, messages, and your profile.
      </div>
    </div>
  );
};

export default ProfilePhotoUploader;